import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Upload, Calendar, MessageSquare, Trash2, Plus, Star, CheckCircle, XCircle, 
  LogOut, Key, Users, Mail, Phone, Image as ImageIcon, PartyPopper, Pencil, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const resolveMediaUrl = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

export const Admin = () => {
  const { user, logout, changePassword } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [partyInquiries, setPartyInquiries] = useState([]);
  const [teamInquiries, setTeamInquiries] = useState([]);
  
  // Form states
  const [newEvent, setNewEvent] = useState({
    title: '', date: '', time: '', duration: '', spots: '', spotsAvailable: '', description: '', featured: false, isPopup: false
  });
  const [editingEventId, setEditingEventId] = useState(null);
  const [newImage, setNewImage] = useState({ title: '', description: '', url: '' });
  const [uploadForm, setUploadForm] = useState({ file: null, title: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const [blockForm, setBlockForm] = useState({ date: '', time: '', allDay: false });
  const [promoForm, setPromoForm] = useState({ message: '', active: false });
  const [blocking, setBlocking] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, testimonialsRes, bookingsRes, eventsRes, portfolioRes, partiesRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/contacts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/testimonials`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/bookings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/portfolio`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/party-inquiries`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/team-inquiries`, { withCredentials: true })
      ]);
      
      setContacts(contactsRes.data.data || []);
      setTestimonials(testimonialsRes.data.data || []);
      setBookings(bookingsRes.data.data || []);
      setEvents(eventsRes.data.data || []);
      setPortfolioImages(portfolioRes.data.data || []);
      setPartyInquiries(partiesRes.data.data || []);
      setTeamInquiries(teamsRes.data.data || []);
      
      try {
        const promoRes = await axios.get(`${API_URL}/api/promo`);
        setPromoForm({ message: promoRes.data.message || '', active: !!promoRes.data.active });
      } catch (e) { /* promo optional */ }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleApproveTestimonial = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/admin/testimonials/${id}/approve`, {}, { withCredentials: true });
      toast.success('Testimonial approved!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve testimonial');
    }
  };

  const handleRejectTestimonial = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/admin/testimonials/${id}/reject`, {}, { withCredentials: true });
      toast.success('Testimonial rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject testimonial');
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        ...newEvent,
        spots: parseInt(newEvent.spots),
        spotsAvailable: parseInt(newEvent.spotsAvailable || newEvent.spots)
      };
      if (editingEventId) {
        await axios.put(`${API_URL}/api/admin/events/${editingEventId}`, eventData, { withCredentials: true });
        toast.success('Event updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/admin/events`, eventData, { withCredentials: true });
        toast.success('Event added successfully!');
      }
      setNewEvent({ title: '', date: '', time: '', duration: '', spots: '', spotsAvailable: '', description: '', featured: false, isPopup: false });
      setEditingEventId(null);
      fetchAllData();
    } catch (error) {
      toast.error(editingEventId ? 'Failed to update event' : 'Failed to add event');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEventId(event.id);
    setNewEvent({
      title: event.title || '',
      date: event.date || '',
      time: event.time || '',
      duration: event.duration || '',
      spots: String(event.spots ?? ''),
      spotsAvailable: String(event.spotsAvailable ?? ''),
      description: event.description || '',
      featured: !!event.featured,
      isPopup: !!event.isPopup
    });
    document.getElementById('event-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setNewEvent({ title: '', date: '', time: '', duration: '', spots: '', spotsAvailable: '', description: '', featured: false, isPopup: false });
  };

  const handleDeleteEvent = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/events/${id}`, { withCredentials: true });
      toast.success('Event deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/portfolio`, newImage, { withCredentials: true });
      toast.success('Image added successfully!');
      setNewImage({ title: '', description: '', url: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDeleteImage = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/portfolio/${id}`, { withCredentials: true });
      toast.success('Image deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleUploadMedia = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please choose a photo or video file');
      return;
    }
    if (!uploadForm.title) {
      toast.error('Please enter a title');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      await axios.post(`${API_URL}/api/admin/portfolio/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Media uploaded! It now appears in the portfolio.');
      setUploadForm({ file: null, title: '', description: '' });
      document.getElementById('portfolio-file-input').value = '';
      fetchAllData();
    } catch (error) {
      toast.error('Upload failed', {
        description: error.response?.data?.detail || 'Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSavePromo = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/promo`, promoForm, { withCredentials: true });
      toast.success(promoForm.active ? 'Promo banner is now live on the site!' : 'Promo banner saved (currently off)');
    } catch (error) {
      toast.error('Failed to save promo banner');
    }
  };

  const handleBlockSlot = async (e) => {
    e.preventDefault();
    if (!blockForm.date || (!blockForm.allDay && !blockForm.time)) {
      toast.error('Please pick a date and a time (or check "Block entire day")');
      return;
    }
    setBlocking(true);
    try {
      const endpoint = blockForm.allDay ? 'block-day' : 'block';
      const payload = blockForm.allDay ? { date: blockForm.date } : { date: blockForm.date, time: blockForm.time };
      const response = await axios.post(`${API_URL}/api/admin/bookings/${endpoint}`, payload, { withCredentials: true });
      toast.success(response.data.message || 'Blocked!');
      setBlockForm({ date: '', time: '', allDay: false });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block');
    } finally {
      setBlocking(false);
    }
  };

  const handleDeleteBooking = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/bookings/${id}`, { withCredentials: true });
      toast.success('Removed - time slot is available again');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    const result = await changePassword(passwordForm.current, passwordForm.new);
    if (result.success) {
      toast.success('Password changed successfully!');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } else {
      toast.error(result.error || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Welcome back, {user?.email}</p>
          </div>
          <Button
            onClick={logout}
            className="bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Contact Forms</p>
                <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Testimonials</p>
                <p className="text-2xl font-bold text-gray-900">{testimonials.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Portfolio Images</p>
                <p className="text-2xl font-bold text-gray-900">{portfolioImages.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow flex-wrap h-auto">
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="parties" data-testid="admin-tab-parties">Parties</TabsTrigger>
            <TabsTrigger value="teams" data-testid="admin-tab-teams">Teams</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6">Contact Submissions</h2>
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{contact.name}</h3>
                        <p className="text-sm text-gray-600">{contact.email} • {contact.phone}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(contact.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 mt-2">{contact.message}</p>
                  </div>
                ))}
                {contacts.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No contact submissions yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6">Testimonials Management</h2>
              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className={`border rounded-lg p-4 ${
                    testimonial.approved ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{testimonial.name}</h3>
                          <div className="flex">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={`admin-star-${testimonial.id}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{testimonial.email} • {testimonial.sessionAttended}</p>
                        <p className="text-gray-700">"{testimonial.comment}"</p>
                        <p className="text-xs text-gray-500 mt-2">{new Date(testimonial.date).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2">
                        {!testimonial.approved && (
                          <Button
                            size="sm"
                            onClick={() => handleApproveTestimonial(testimonial.id)}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleRejectTestimonial(testimonial.id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {testimonials.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No testimonials yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6">Event Bookings</h2>
              
              {/* Block a Time Slot */}
              <form onSubmit={handleBlockSlot} className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 p-6 rounded-xl mb-6">
                <h3 className="font-semibold text-lg mb-1">🚫 Block a Time Slot</h3>
                <p className="text-sm text-gray-600 mb-4">Mark any date & time as unavailable - customers will see it crossed out instantly.</p>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                    <Input
                      type="date"
                      value={blockForm.date}
                      onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                      data-testid="block-slot-date-input"
                      className="w-44"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Time</label>
                    <select
                      value={blockForm.time}
                      onChange={(e) => setBlockForm({ ...blockForm, time: e.target.value })}
                      data-testid="block-slot-time-select"
                      disabled={blockForm.allDay}
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-400 bg-white h-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select time</option>
                      {Array.from({ length: 28 }, (_, i) => {
                        const hour = 9 + Math.floor(i / 2);
                        const minutes = i % 2 === 0 ? '00' : '30';
                        const h12 = hour > 12 ? hour - 12 : hour;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const label = `${h12}:${minutes} ${ampm}`;
                        return <option key={label} value={label}>{label}</option>;
                      })}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 h-10 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={blockForm.allDay}
                      onChange={(e) => setBlockForm({ ...blockForm, allDay: e.target.checked, time: e.target.checked ? '' : blockForm.time })}
                      data-testid="block-all-day-checkbox"
                      className="w-4 h-4 accent-red-500"
                    />
                    <span className="text-sm font-semibold text-gray-700">Block entire day</span>
                  </label>
                  <Button type="submit" disabled={blocking} className="bg-red-500 hover:bg-red-600" data-testid="block-slot-submit">
                    {blocking ? 'Blocking...' : blockForm.allDay ? 'Block Whole Day' : 'Block Slot'}
                  </Button>
                </div>
              </form>
              
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div key={booking.id} className={`border rounded-lg p-4 ${booking.status === 'blocked' ? 'border-red-200 bg-red-50/50' : 'border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          {booking.event_title}
                          {booking.status === 'blocked' && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">BLOCKED</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600">{booking.event_date} at {booking.event_time}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                          {new Date(booking.booked_at).toLocaleString()}
                        </span>
                        {booking.status === 'blocked' && (
                          <Button
                            size="sm"
                            onClick={() => handleDeleteBooking(booking.id)}
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`unblock-slot-${booking.id}`}
                          >
                            Unblock
                          </Button>
                        )}
                      </div>
                    </div>
                    {booking.status !== 'blocked' && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="font-semibold">{booking.customer_name}</p>
                        <p className="text-sm text-gray-600">{booking.customer_email} • {booking.customer_phone}</p>
                        <p className="text-sm text-gray-600" data-testid="booking-participants">
                          {booking.participants ?? 1} participant(s)
                          {booking.adults != null && booking.children != null && ` — ${booking.adults} Adult(s), ${booking.children} Child(ren)`}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
                {bookings.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No bookings yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6">Events Management</h2>
              
              {/* Add Event Form */}
              <form id="event-form" onSubmit={handleAddEvent} className="bg-gradient-to-br from-pink-50 to-yellow-50 p-6 rounded-xl mb-6">
                <h3 className="font-semibold text-lg mb-4">{editingEventId ? '✏️ Edit Event' : 'Add New Event'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Event Title"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    required
                  />
                  <Input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                    required
                  />
                  <Input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    required
                  />
                  <Input
                    placeholder="Duration (e.g., 2 hours)"
                    value={newEvent.duration}
                    onChange={(e) => setNewEvent({...newEvent, duration: e.target.value})}
                    required
                  />
                  <Input
                    type="number"
                    placeholder="Total Spots"
                    value={newEvent.spots}
                    onChange={(e) => setNewEvent({...newEvent, spots: e.target.value, spotsAvailable: e.target.value})}
                    required
                  />
                  <Textarea
                    placeholder="Description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    className="md:col-span-2"
                  />
                </div>
                <div className="flex gap-4 mt-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newEvent.isPopup}
                      onChange={(e) => setNewEvent({...newEvent, isPopup: e.target.checked})}
                    />
                    <span className="text-sm">Pop-up Event</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newEvent.featured}
                      onChange={(e) => setNewEvent({...newEvent, featured: e.target.checked})}
                    />
                    <span className="text-sm">Featured</span>
                  </label>
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="mt-4 bg-gradient-to-r from-pink-400 to-pink-500" data-testid="admin-event-submit">
                    {editingEventId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    {editingEventId ? 'Save Changes' : 'Add Event'}
                  </Button>
                  {editingEventId && (
                    <Button type="button" onClick={handleCancelEdit} className="mt-4 bg-gray-400 hover:bg-gray-500">
                      Cancel
                    </Button>
                  )}
                </div>
              </form>

              {/* Events List */}
              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{event.title}</h3>
                        <p className="text-sm text-gray-600">{event.date} at {event.time} • {event.duration}</p>
                        <p className="text-sm text-gray-600 mt-1">{event.spotsAvailable}/{event.spots} spots available</p>
                        <p className="text-gray-700 mt-2">{event.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleEditEvent(event)}
                          className="bg-blue-500 hover:bg-blue-600"
                          data-testid={`admin-event-edit-${event.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDeleteEvent(event.id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No events yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6">Portfolio Management</h2>
              
              {/* Upload Media Form */}
              <form onSubmit={handleUploadMedia} className="bg-gradient-to-br from-yellow-50 to-pink-50 p-6 rounded-xl mb-6 border-2 border-yellow-300">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-pink-500" />
                  Upload Photo or Video
                </h3>
                <div className="space-y-4">
                  <input
                    id="portfolio-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] || null })}
                    data-testid="portfolio-upload-file-input"
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-pink-500 file:text-white hover:file:bg-pink-600 file:cursor-pointer"
                  />
                  <Input
                    placeholder="Title"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    data-testid="portfolio-upload-title-input"
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    data-testid="portfolio-upload-description-input"
                  />
                </div>
                <Button type="submit" disabled={uploading} className="mt-4 bg-gradient-to-r from-pink-500 to-purple-500" data-testid="portfolio-upload-submit">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload to Portfolio'}
                </Button>
                <p className="text-xs text-gray-500 mt-2">JPG, PNG, WEBP, GIF, MP4, MOV or WEBM • max 60MB • appears on the Portfolio page instantly</p>
              </form>
              
              {/* Add Image Form */}
              <form onSubmit={handleAddImage} className="bg-gradient-to-br from-blue-50 to-pink-50 p-6 rounded-xl mb-6">
                <h3 className="font-semibold text-lg mb-4">Add New Image</h3>
                <div className="space-y-4">
                  <Input
                    placeholder="Image Title"
                    value={newImage.title}
                    onChange={(e) => setNewImage({...newImage, title: e.target.value})}
                    required
                  />
                  <Input
                    placeholder="Image URL"
                    value={newImage.url}
                    onChange={(e) => setNewImage({...newImage, url: e.target.value})}
                    required
                  />
                  <Input
                    placeholder="Description"
                    value={newImage.description}
                    onChange={(e) => setNewImage({...newImage, description: e.target.value})}
                  />
                </div>
                <Button type="submit" className="mt-4 bg-gradient-to-r from-blue-400 to-blue-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Image
                </Button>
              </form>

              {/* Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {portfolioImages.map((image) => (
                  <div key={image.id} className="relative group">
                    {image.type === 'video' ? (
                      <video
                        src={resolveMediaUrl(image.url)}
                        className="w-full h-48 object-cover rounded-lg"
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={resolveMediaUrl(image.url)}
                        alt={image.title}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <Button
                        size="sm"
                        onClick={() => handleDeleteImage(image.id)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                    <p className="text-sm font-semibold mt-2">{image.title}</p>
                  </div>
                ))}
                {portfolioImages.length === 0 && (
                  <p className="text-center text-gray-500 py-8 col-span-3">No portfolio images yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Party Inquiries Tab */}
          <TabsContent value="parties">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <PartyPopper className="w-6 h-6 text-pink-500" />
                Birthday Party Inquiries
              </h2>
              <div className="space-y-4">
                {partyInquiries.map((inquiry) => (
                  <div key={inquiry.id} className="border border-pink-200 bg-pink-50/50 rounded-lg p-4" data-testid="admin-party-inquiry">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{inquiry.parent_name}</h3>
                        <p className="text-sm text-gray-600">{inquiry.email} • {inquiry.phone}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(inquiry.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-sm">
                      <p><strong>Party Date:</strong> {inquiry.party_date}</p>
                      <p><strong>Child's Age:</strong> {inquiry.child_age}</p>
                      <p><strong>Group Size:</strong> {inquiry.group_size}</p>
                    </div>
                    {inquiry.package && (
                      <p className="text-sm mt-2"><strong>Package:</strong> <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">{inquiry.package}</span></p>
                    )}
                    {inquiry.message && <p className="text-gray-700 mt-2 text-sm italic">"{inquiry.message}"</p>}
                  </div>
                ))}
                {partyInquiries.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No party inquiries yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Team Inquiries Tab */}
          <TabsContent value="teams">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Users className="w-6 h-6 text-blue-500" />
                Team Building Inquiries
              </h2>
              <div className="space-y-4">
                {teamInquiries.map((inquiry) => (
                  <div key={inquiry.id} className="border border-blue-200 bg-blue-50/50 rounded-lg p-4" data-testid="admin-team-inquiry">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{inquiry.company_name}</h3>
                        <p className="text-sm text-gray-600">{inquiry.contact_name} • {inquiry.email} • {inquiry.phone}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(inquiry.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 text-sm">
                      <p><strong>Team Size:</strong> {inquiry.team_size}</p>
                      <p><strong>Preferred Dates:</strong> {inquiry.preferred_dates}</p>
                    </div>
                    {inquiry.message && <p className="text-gray-700 mt-2 text-sm italic">"{inquiry.message}"</p>}
                  </div>
                ))}
                {teamInquiries.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No team building inquiries yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold mb-6">Account Settings</h2>
              
              {/* Promo Banner */}
              <div className="bg-gradient-to-br from-yellow-50 to-purple-50 border-2 border-yellow-300 p-6 rounded-xl mb-8">
                <h3 className="font-semibold text-lg mb-1">✨ Special Offer Banner</h3>
                <p className="text-sm text-gray-600 mb-4">Show a limited-time promo message at the top of every page on the site.</p>
                <div className="space-y-3 max-w-xl">
                  <Input
                    placeholder='e.g., "🎉 20% off all sessions this weekend - book now!"'
                    value={promoForm.message}
                    onChange={(e) => setPromoForm({ ...promoForm, message: e.target.value })}
                    maxLength={200}
                    data-testid="promo-message-input"
                  />
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={promoForm.active}
                      onChange={(e) => setPromoForm({ ...promoForm, active: e.target.checked })}
                      data-testid="promo-active-checkbox"
                      className="w-4 h-4 accent-pink-500"
                    />
                    <span className="text-sm font-semibold text-gray-700">Show banner on the site</span>
                  </label>
                  <Button onClick={handleSavePromo} className="bg-gradient-to-r from-yellow-500 to-pink-500" data-testid="promo-save-button">
                    <Save className="w-4 h-4 mr-2" />
                    Save Banner
                  </Button>
                </div>
              </div>
              
              <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="bg-gradient-to-r from-pink-400 to-pink-500">
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
